import type { ReactNode } from 'react'

/**
 * SearchableSelect validation states
 */
export type SearchableSelectValidationState = 'default' | 'valid' | 'invalid'

/**
 * Option type for SearchableSelect
 */
export interface SelectOption<T = string> {
  /** Unique value for the option */
  value: T
  /** Display label */
  label: string
  /** Optional secondary text */
  description?: string
  /** Whether option is disabled */
  disabled?: boolean
  /** Custom data for formatting */
  data?: Record<string, unknown>
}

export interface SearchableSelectProps<T = string> {
  /** Options to display */
  options: SelectOption<T>[]
  /** Selected value */
  value?: T | null
  /** Callback when value changes */
  onChange?: (value: T | null, option: SelectOption<T> | null) => void
  /** Placeholder text */
  placeholder?: string
  /** Search placeholder text */
  searchPlaceholder?: string
  /** Validation state */
  validationState?: SearchableSelectValidationState
  /** Error message */
  error?: string
  /** Helper text */
  helperText?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Hide the error/helper text area (when used inside FormField) */
  hideMessage?: boolean
  /** Allow clearing the selection */
  clearable?: boolean
  /** Custom option renderer */
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  /** Custom selected value renderer */
  renderValue?: (option: SelectOption<T>) => ReactNode
  /** Filter function for search */
  filterOption?: (option: SelectOption<T>, searchQuery: string) => boolean
  /** No options text */
  noOptionsText?: string
  /** Loading state */
  isLoading?: boolean
  /** Enable virtualization for large lists */
  virtualized?: boolean
  /** Item height for virtualization (default: 38) */
  itemHeight?: number
  /** Maximum visible items (default: 8) */
  maxVisibleItems?: number
  /** Whether the select is disabled */
  disabled?: boolean
  /** ID for accessibility */
  id?: string
  /** Custom class name */
  className?: string
  /** Show as centered modal on desktop instead of attached dropdown */
  modal?: boolean
  /** Callback when dropdown open state changes */
  onOpenChange?: (isOpen: boolean) => void
  /** Optional footer content (e.g., "Add New" button). Can be a render function receiving a close callback. */
  footerAction?: ReactNode | ((close: () => void) => ReactNode)
}

export const sizeClassMap = {
  sm: 'el-input-sm',
  md: 'el-input-md',
  lg: 'el-input-lg',
}

export const validationClassMap: Record<SearchableSelectValidationState, string> = {
  default: '',
  valid: 'el-input-valid',
  invalid: 'el-input-invalid',
}

/**
 * Default filter function - searches label and description
 */
export function defaultFilterOption<T>(option: SelectOption<T>, searchQuery: string): boolean {
  const query = searchQuery.toLowerCase()
  return (
    option.label.toLowerCase().includes(query) ||
    (option.description?.toLowerCase().includes(query) ?? false)
  )
}
